import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';
import { useMatch, useParams } from 'react-router-dom';
import { v4 } from 'uuid';

import { gql } from '@graphql/__generated__';
import { type EmailTemplateInput } from '@modules/_shared/components/EmailTemplateForm';
import {
  InDrawerSidebar,
  type InDrawerSidebarItem,
  type InDrawerSidebarRoutesDefinitions,
} from '@modules/_shared/components/InDrawerSidebar';
import { createEmailTemplateMutationData } from '@modules/_shared/components/listing-marketing/emailTemplateMutation';
import { EnquiryResponseEmailForm } from '@modules/_shared/components/listing-marketing/EnquiryResponseEmailForm';
import { ListingMlsSettings } from '@modules/_shared/components/listing-marketing/ListingMlsSettings';
import { UPSERT_EMAIL_TEMPLATE } from '@modules/_shared/components/listing-marketing/queries';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { canAccessMls } from '@modules/_shared/utils/access';
import { ListingBuyersAutoMatching } from '@modules/listings/components/ListingBuyersAutoMatching';
import { ListingPortalsSettings } from '@modules/listings/components/ListingPortalsSettings';
import { GET_AVAILABLE_PORTALS } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

const GET_LISTING_MARKETING_QUERY = gql(/* GraphQL */ `
  query GetListingMarketing($listingId: uuid!) {
    lots_by_pk(id: $listingId) {
      id
      status
      offer_type
      shared_on_mls_at
      ...PortalListingButton
      ...MarketingFormData
      enable_auto_matching
      auto_matching_filters
      send_enquiries_to_sellers
      broker {
        id
        teams_users {
          team_id
          user_id
        }
      }
      ...ListingEnquiryResponseEmail
      ...EmailTemplateFormLotSnippetData
    }
  }
`);

const UPDATE_LISTING = gql(/* GraphQL */ `
  mutation updateEnquiryReponseEmail($id: uuid!, $lot: lots_set_input) {
    update_lots_by_pk(pk_columns: { id: $id }, _set: $lot) {
      id
      ...ListingEnquiryResponseEmail
    }
  }
`);

export const MarketingHomogeneousTitle: React.FC<{ title: string }> = ({
  title,
}) => (
  <Box
    sx={{
      borderBottom: '1px solid',
      borderColor: 'grey.400',
      mb: 2,
    }}
  >
    <Typography variant="h6" component="h3">
      {title}
    </Typography>
  </Box>
);

type SidebarPath =
  | 'portals'
  | 'auto-matching'
  | 'enquiry-response'
  | 'mls-network';

export const ListingMarketing: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const { t, countryCode } = useLocale();
  const { me } = useAppData();
  const isMlsEnabled = canAccessMls(me, countryCode);
  const { lotId } = useParams();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const matchListing = useMatch('/listings/:lotId/marketing/:menuItem/*');
  const matchDevelopment = useMatch(
    '/developments/:developmentId/lots/:lotId/marketing/:menuItem/*',
  );
  const matchDashboard = useMatch(
    '/dashboard/listings/:lotId/marketing/:menuItem/*',
  );

  const selectedItem =
    matchListing?.params.menuItem ??
    matchDevelopment?.params.menuItem ??
    matchDashboard?.params.menuItem;

  const { data, loading, error } = useQuery(GET_LISTING_MARKETING_QUERY, {
    skip: lotId == null,
    variables: { listingId: lotId ?? '' },
  });

  const listing = data?.lots_by_pk;
  const { data: portalsData, loading: portalsLoading } = useQuery(
    GET_AVAILABLE_PORTALS,
    {
      variables: {
        team_ids: listing?.broker?.teams_users.map(tu => tu.team_id) ?? [],
        lot_id: listing?.id ?? '',
      },
      skip: listing == null,
    },
  );

  const [updateListing] = useMutation(UPDATE_LISTING, {
    onError: setErrorLike,
  });
  const [upsertEmailTemplate] = useMutation(UPSERT_EMAIL_TEMPLATE, {
    onError: setErrorLike,
  });

  const onEnquiryResponseEnabledChange = useCallback(
    async (enabled: boolean) => {
      if (listing == null) {
        return false;
      }

      const result = await updateListing({
        variables: {
          id: listing.id,
          lot: { enquiry_response_enabled: enabled },
        },
      });

      return (
        result.data?.update_lots_by_pk?.enquiry_response_enabled ??
        listing.enquiry_response_enabled ??
        false
      );
    },
    [updateListing, listing],
  );

  const onSubmitEmailTemplate = useCallback(
    async (input: EmailTemplateInput) => {
      const emailTemplateId = input.id ?? v4();
      if (listing == null) {
        return { success: false };
      }

      const result = await upsertEmailTemplate({
        variables: {
          listingId: listing.id,
          emailTemplateId,
          ...createEmailTemplateMutationData({
            emailTemplateId,
            input,
            owner: { listingId: listing.id },
            type: 'enquiry_auto_response',
          }),
          listingUpdates: {
            response_sender_id: input.from ?? null,
          },
        },
      });

      return { success: result.data?.insert_email_templates_one != null };
    },
    [listing, upsertEmailTemplate],
  );

  const allPortals = portalsData?.portals ?? [];

  const sidebarItems = useMemo<InDrawerSidebarItem<SidebarPath>[]>(
    () => [
      {
        label: t('Portals'),
        path: 'portals',
        secondary: t('{{count}} available - {{count_published}} published', {
          count: allPortals.length,
          count_published:
            listing?.portal_listings.filter(pl => pl.live).length ?? 0,
        }),
      },
      {
        label: t('Buyers auto-matching'),
        path: 'auto-matching',
        secondary: listing?.enable_auto_matching
          ? t('Auto-matching enabled')
          : t('Auto-matching disabled'),
      },
      {
        label: t('Enquiry response email'),
        path: 'enquiry-response',
        secondary: listing?.enquiry_response_enabled
          ? t('Auto-response enabled')
          : t('Auto-response disabled'),
      },
      ...(isMlsEnabled
        ? [
            {
              label: t('MLS Network'),
              path: 'mls-network' as const,
              secondary:
                listing?.shared_on_mls_at != null
                  ? t('Shared on MLS')
                  : t('Not shared on MLS'),
            },
          ]
        : []),
    ],
    [t, listing, allPortals.length, isMlsEnabled],
  );

  const routesDefinitions = useMemo<
    InDrawerSidebarRoutesDefinitions<SidebarPath>
  >(
    () => ({
      portals: {
        element: listing && (
          <ListingPortalsSettings lotId={listing.id} readOnly={readOnly} />
        ),
        DrawerProps: {
          title: t('Portals'),
        },
      },
      'auto-matching': {
        element: listing && (
          <ListingBuyersAutoMatching lotId={listing.id} readOnly={readOnly} />
        ),
        DrawerProps: {
          title: t('Buyers auto-matching'),
        },
      },
      'enquiry-response': {
        element: listing && (
          <EnquiryResponseEmailForm
            mode="listing"
            listing={listing}
            isFromDevelopment={matchDevelopment != null}
            lotSnippetData={listing}
            onEnquiryResponseEnabledChange={onEnquiryResponseEnabledChange}
            onSubmit={onSubmitEmailTemplate}
            readOnly={readOnly}
          />
        ),
        DrawerProps: {
          title: t('Enquiry response email'),
        },
      },
      'mls-network': {
        element: listing && (
          <ListingMlsSettings lotId={listing.id} readOnly={readOnly} />
        ),
        DrawerProps: {
          title: t('MLS Network'),
        },
      },
    }),
    [
      listing,
      matchDevelopment,
      onEnquiryResponseEnabledChange,
      onSubmitEmailTemplate,
      t,
      readOnly,
    ],
  );

  return (
    <>
      {loading || portalsLoading ? (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {error != null && (
            <Alert severity="error" sx={{ m: 2 }}>
              <pre>{JSON.stringify(error, null, 2)}</pre>
            </Alert>
          )}
          {listing == null ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {t('No listing data found')}
            </Alert>
          ) : (
            <InDrawerSidebar
              items={sidebarItems}
              selectedItem={selectedItem}
              routesDefinitions={routesDefinitions}
              redirectOnIndex="portals"
            />
          )}
          <MutationErrorSnackbar
            error={apolloError}
            onClose={() => setErrorLike(null)}
          />
        </Box>
      )}
    </>
  );
};
